/**
 * Persist server-driven screening session for /fa/intake.
 */
const STORAGE_KEY = 'rb_screening_session'

function emptyStore() {
  return {
    intakeId: null,
    clientSeq: 0,
    writeRevision: 0,
    session: null,
    snapshotId: null,
    s1Intent: null,
    needLabel: null,
    needLabelsById: {},
    /** 'clarifySafety' when MORE_ANSWERS came from R8=UNSURE; else null */
    dispMoreVariant: null,
  }
}

/** Mark disp/more copy variant before divert (R8 UNSURE → clarifySafety). */
export function setDispMoreVariantFromAnswer(questionId, answerIds) {
  const qid = String(questionId || '')
  const ids = Array.isArray(answerIds) ? answerIds.map(String) : []
  if (qid === 'R8' && ids.indexOf('UNSURE') !== -1) {
    return saveScreeningStore({dispMoreVariant: 'clarifySafety'})
  }
  return loadScreeningStore()
}

export function getDispMoreVariant() {
  const v = loadScreeningStore().dispMoreVariant
  return v === 'clarifySafety' ? 'clarifySafety' : null
}

export function loadScreeningStore() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return emptyStore()
    }
    const data = JSON.parse(raw)
    return Object.assign(emptyStore(), data || {})
  } catch (e) {
    return emptyStore()
  }
}

export function saveScreeningStore(partial) {
  const next = Object.assign(loadScreeningStore(), partial || {})
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  return next
}

export function clearScreeningStore() {
  sessionStorage.removeItem(STORAGE_KEY)
}

/** Clear finalized snapshot but keep nothing else — full restart helper. */
export function clearFinalizedSnapshot() {
  return saveScreeningStore({snapshotId: null})
}

export function restartScreeningStore() {
  clearScreeningStore()
  return emptyStore()
}

export function applySessionToStore(session, extras) {
  if (!session || !session.intake_id) {
    return loadScreeningStore()
  }
  // Prefills may contain private safety answers. Keep them only in the active
  // form, never sessionStorage (including the cached session response).
  const cachedSession = Object.assign({}, session)
  delete cachedSession.answer_prefill
  delete cachedSession.review
  const patch = Object.assign({
    intakeId: session.intake_id,
    clientSeq: Number(session.client_seq || 0),
    writeRevision: Number(session.write_revision || 0),
    session: cachedSession,
  }, extras || {})
  if (Object.prototype.hasOwnProperty.call(session, 's1_intent')
      && !Object.prototype.hasOwnProperty.call(extras || {}, 's1Intent')) {
    patch.s1Intent = session.s1_intent || null
  }
  return saveScreeningStore(patch)
}

export function getFinalizedSnapshotId() {
  const store = loadScreeningStore()
  return store.snapshotId || null
}

export function setFinalizedSnapshot(snapshotId, intakeId) {
  return saveScreeningStore({
    snapshotId: snapshotId || null,
    intakeId: intakeId || loadScreeningStore().intakeId,
  })
}

export function nextClientSeq() {
  return Number(loadScreeningStore().clientSeq || 0) + 1
}

export function currentWriteRevision() {
  return Number(loadScreeningStore().writeRevision || 0)
}

/** Terminal / disposition helpers (Assets statuses). */
export function isUrgentCrisisStatus(status) {
  return [
    'URGENT_SAFETY_PATHWAY',
    'BEHAVIORAL_HEALTH_PATHWAY',
  ].indexOf(status) !== -1
}

/** Any terminal that blocks routine match (crisis + specialist + private). */
export function isSafetyTerminalStatus(status) {
  return isUrgentCrisisStatus(status)
    || status === 'SPECIALIST_PATHWAY'
    || status === 'PRIVATE_SAFETY_FOLLOWUP'
}

export function isMatchReadyStatus(status) {
  return status === 'MATCH_READY'
}

/**
 * Map Assets terminal_status / finalize status → intake route.
 * Urgency policy:
 * - URGENT / BEHAVIORAL → crisis contacts (intakeCrisis)
 * - PRIVATE → disposition, urgent styling (not routine match)
 * - SPECIALIST → disposition, non-urgent guidance (not crisis contacts)
 * - MORE_ANSWERS → clarification recovery (resume screening)
 * - ROUTE_UNSUPPORTED → unsupported combination disposition
 *
 * OPERATIONS_SUPPORT is not emitted by the V2 screening backend; unused
 * client-only mapping removed so every divert matches a real terminal_status.
 */
export function divertRouteForStatus(status) {
  if (!status) {
    return null
  }
  if (status === 'SPECIALIST_PATHWAY') {
    return {name: 'intakeDisp', params: {kind: 'specialist'}}
  }
  if (status === 'PRIVATE_SAFETY_FOLLOWUP') {
    return {name: 'intakeDisp', params: {kind: 'private'}}
  }
  if (isUrgentCrisisStatus(status)) {
    return {name: 'intakeCrisis'}
  }
  if (status === 'MORE_ANSWERS_NEEDED') {
    return {name: 'intakeDisp', params: {kind: 'more'}}
  }
  if (status === 'ROUTE_UNSUPPORTED') {
    return {name: 'intakeDisp', params: {kind: 'unsupported'}}
  }
  return null
}

export function divertRouteForSession(session) {
  return divertRouteForStatus(session && (session.terminal_status || null))
}

/**
 * MORE_ANSWERS clarification recovery: resume the same intake when present.
 * Falls back to a full restart only when no intake id remains.
 * Callers should prev() a terminal intake before showing screening,
 * otherwise the terminal status immediately re-diverts to disp/more.
 */
export function clarificationRecoveryRoute(lang) {
  const store = loadScreeningStore()
  const params = {lang: lang || 'fa'}
  if (store.intakeId) {
    return {name: 'intakeScreening', params}
  }
  return {name: 'intakeScreening', params, query: {restart: '1'}}
}

/** Clear clarify-safety variant after successfully reopening the safety question. */
export function clearDispMoreVariant() {
  return saveScreeningStore({dispMoreVariant: null})
}

/**
 * Explicit urgency for disposition kinds used by IntakeDispStep.
 * Specialist is guidance-only (not urgent). Private + crisis remain urgent.
 */
export function dispositionKindIsUrgent(kind) {
  return kind === 'private' || kind === 'crisis'
}

/**
 * Finalized / immutable MORE_ANSWERS cannot be reopened via /prev.
 * Interactive MORE_ANSWERS (IN_PROGRESS + terminal_status) still can.
 */
export function isImmutableMoreAnswersIntake(session) {
  if (!session) {
    return false
  }
  const state = String(session.state || '')
  if (state === 'FINALIZED') {
    return true
  }
  if (state.indexOf('TERMINAL_') === 0) {
    const status = session.terminal_status || session.status || session.sm_state
    return status === 'MORE_ANSWERS_NEEDED'
      || state === 'TERMINAL_MORE_ANSWERS_NEEDED'
  }
  return false
}

